import { PlanetName, CompleteKundali, Language, PlanetPosition, KundaliChartData } from '../types/kundali';
import { evaluateParashariLordship } from './remediesBankEngine';
import { ZODIAC_SIGNS } from './vedicEngine';

export interface PlanetaryRemedyDetail {
  planet: PlanetName;
  sanskritName: string;
  role: string;
  functionalStatus: string;
  isBenefic: boolean;
  priority: 'High' | 'Medium' | 'Low';
  
  // Somatic & Lifestyle Behavioral Upaya (Core User Request)
  somaticBehavioral: {
    en: string;
    hi: string;
    category: 'Physical Activity' | 'Psychological & Breathwork' | 'Dietary Alignment' | 'Habitual Discipline';
  };
  
  // Classical Foundation Upaya
  gemstoneAndAlternative: {
    primaryGem: string;
    substituteGem: string;
    metal: string;
    wearingFinger: string;
    suitabilityNote: { en: string; hi: string };
  };
  
  mantraUpasana: {
    vedicMantra: string;
    tantricBeejMantra: string;
    recitationCount: number;
    recommendedTime: string;
  };
  
  daanAndCharity: {
    itemsToDonate: { en: string; hi: string };
    recipientOrLocation: { en: string; hi: string };
    idealDay: string;
    warningNote?: { en: string; hi: string };
  };
  
  deityAndDevotion: {
    primaryDeity: { en: string; hi: string };
    stotram: string;
    fastingDay: { en: string; hi: string };
  };
}

export interface DynamicTimingRemedy {
  contextType: 'Mahadasha' | 'Antardasha' | 'Pratyantardasha' | 'SadeSati' | 'GocharTransit' | 'BadhakaMaraka';
  planet: PlanetName;
  titleEn: string;
  titleHi: string;
  urgency: 'Immediate' | 'Active Window' | 'Supportive';
  timeframe: string;
  impactZone: string;
  
  // Tailored Karmic Remediation for this exact dynamic period
  actionPlan: {
    en: string;
    hi: string;
  };
  somaticAction: {
    en: string;
    hi: string;
  };
  daanOrPuja: {
    en: string;
    hi: string;
  };
}

export interface ComprehensiveRemedialReport {
  lagnaSign: string;
  currentDashaSummary: string;
  // 1. View 1: Cumulative Natal Foundation Upaya
  cumulativePlanetaryRemedies: PlanetaryRemedyDetail[];
  // 2. View 2: Dynamic Current Overall Upaya (Gochar & Dasha contextual)
  dynamicActionPlan: DynamicTimingRemedy[];
  // House-specific active remedial recommendations
  houseRemedies: Record<number, {
    houseNum: number;
    houseLord: PlanetName;
    occupants: PlanetName[];
    dashaInfluenced: boolean;
    primaryKarmicFocus: { en: string; hi: string };
    behavioralAdjustment: { en: string; hi: string };
  }>;
}

// Classical Behavioral & Somatic remedies dictionary for all planets
const PLANETARY_SOMATIC_RULES: Record<PlanetName, {
  somaticEn: string;
  somaticHi: string;
  category: 'Physical Activity' | 'Psychological & Breathwork' | 'Dietary Alignment' | 'Habitual Discipline';
  gem: string;
  subGem: string;
  metal: string;
  finger: string;
  vedicMantra: string;
  beejMantra: string;
  daanEn: string;
  daanHi: string;
  recipientEn: string;
  recipientHi: string;
  day: string;
  deityEn: string;
  deityHi: string;
  stotram: string;
  fastDayEn: string;
  fastDayHi: string;
}> = {
  Sun: {
    somaticEn: 'Surya Namaskar at sunrise (12 cycles), early morning outdoor light exposure, practicing upright posture and direct eye contact in leadership roles.',
    somaticHi: 'सूर्योदय के समय सूर्य नमस्कार (12 चक्र), प्रातःकालीन धूप का सेवन, रीढ़ सीधी रखना एवं आत्मविश्वासपूर्ण नेत्र-संवाद का अभ्यास।',
    category: 'Physical Activity',
    gem: 'Ruby (Manikya)',
    subGem: 'Red Garnet / Star Ruby',
    metal: 'Copper or Gold',
    finger: 'Ring Finger (Anamika)',
    vedicMantra: 'ॐ आकृष्णेन रजसा वर्तमानो निवेशयन्नमृतं मर्त्यं च। हिरण्ययेन सविता रथेना देवो याति भुवनानि पश्यन्॥',
    beejMantra: 'ॐ ह्रां ह्रीं ह्रौं सः सूर्याय नमः (Om Hraam Hreem Hroum Sah Suryaya Namah)',
    daanEn: 'Wheat, jaggery (gur), copper vessels, red sandalwood, ruby to fathers or temple priests.',
    daanHi: 'गेहूं, गुड़, तांबे के बर्तन, लाल चंदन, पिता तुल्य व्यक्तियों या मंदिर में दान।',
    recipientEn: 'Father figures, spiritual gurus, or charitable institutions on Sunday mornings.',
    recipientHi: 'रविवार प्रातः पिता, पूज्य गुरुजन या धार्मिक संस्थान।',
    day: 'Sunday',
    deityEn: 'Lord Shiva / Lord Rama / Surya Narayana',
    deityHi: 'भगवान शिव / प्रभु श्रीराम / श्री सूर्यनारायण',
    stotram: 'Aditya Hridaya Stotram',
    fastDayEn: 'Sunday (avoid salt if possible)',
    fastDayHi: 'रविवार (यथासंभव नमक-रहित व्रत)'
  },
  Moon: {
    somaticEn: 'Mindfulness meditation (Vipassana or Trataka), deep diaphragmatic breathing (Pranayama - Anulom Vilom), sound water hydration, sleeping by 10:30 PM, journaling emotions.',
    somaticHi: 'माइंडफुलनेस ध्यान, अनुलोम-विलोम प्राणायाम, पर्याप्त जलपान (चांदी के पात्र से), समय पर शयन एवं भावनाओं की दैनिक डायरी लिखना।',
    category: 'Psychological & Breathwork',
    gem: 'Natural Pearl (Moti)',
    subGem: 'Moonstone / White Coral',
    metal: 'Silver',
    finger: 'Little Finger (Kanishtha) or Ring Finger',
    vedicMantra: 'ॐ इमं देवा असपत्नꣳ सुवध्वं महते क्षत्राय महते ज्येष्ठ्याय महते जानराज्यायेन्द्रस्येन्द्रियाय।',
    beejMantra: 'ॐ श्रां श्रीं श्रौं सः चन्द्रमसे नमः (Om Shraam Shreem Shroum Sah Chandramase Namah)',
    daanEn: 'Rice, milk, white clothing, silver, sugar, camphor to maternal figures or orphanages.',
    daanHi: 'चावल, दूध, श्वेत वस्त्र, चांदी, मिश्री, कपूर का माता, कन्याओं या असहायों को दान।',
    recipientEn: 'Mother, elderly women, or hermits on Monday evenings.',
    recipientHi: 'माता, वयोवृद्ध महिलाएं अथवा सोमवार सायंकाल सात्विक व्यक्ति।',
    day: 'Monday',
    deityEn: 'Goddess Parvati / Lord Shiva (Chandrashekhara)',
    deityHi: 'माता पार्वती / भगवान चंद्रशेखर',
    stotram: 'Shri Chandra Kavacham & Shiva Mahimna Stotram',
    fastDayEn: 'Monday (milk and fruit diet)',
    fastDayHi: 'सोमवार (दुग्ध व फलाहार)'
  },
  Mars: {
    somaticEn: 'High-intensity interval training (HIIT), weight lifting, martial arts, outdoor running, avoiding reckless impulsive responses, channelizing assertiveness into constructive sports.',
    somaticHi: 'नियमित व्यायाम, भारोत्तोलन (जिम), दौड़ या मार्शल आर्ट्स; क्रोध और जल्दबाजी पर संयम रखकर ऊर्जा को उत्पादक कार्यों में लगाना।',
    category: 'Physical Activity',
    gem: 'Red Coral (Moonga)',
    subGem: 'Carnelian / Red Agate',
    metal: 'Copper or Gold',
    finger: 'Ring Finger (Anamika)',
    vedicMantra: 'ॐ अग्निर्मूर्धा दिवः ककुत्पतिः पृथिव्या अयम्। अपाꣳ रेताꣳसि जिन्वति॥',
    beejMantra: 'ॐ क्रां क्रीं क्रौं सः भौमाय नमः (Om Kraam Kreem Kroum Sah Bhaumaya Namah)',
    daanEn: 'Red lentils (Masoor Dal), jaggery, copper, sweet bread (Roti) to brothers or military/firefighters.',
    daanHi: 'लाल मसूर की दाल, गुड़, तांबा, मीठी रोटियां, रक्तदान अथवा सेवा कार्यों में दान।',
    recipientEn: 'Brothers, soldiers, gym workers, or Hanuman temple on Tuesdays.',
    recipientHi: 'भाई, सैनिक, सेवादार या मंगलवार को हनुमान मंदिर।',
    day: 'Tuesday',
    deityEn: 'Lord Hanuman / Lord Kartikeya (Murugan)',
    deityHi: 'श्री हनुमान जी / भगवान कार्तिकेय',
    stotram: 'Hanuman Chalisa, Bajrang Baan & Sundarkand',
    fastDayEn: 'Tuesday (sweet meals without salt)',
    fastDayHi: 'मंगलवार (बिना नमक का मीठा भोजन)'
  },
  Mercury: {
    somaticEn: 'Daily reading of intellectual literature, memory games, solving puzzles, vocal training or singing, walking barefoot on green grass, practicing non-violent and witty speech.',
    somaticHi: 'दैनिक स्वाध्याय, पहेलियां हल करना, गायन या वाक-प्रशिक्षण, हरी घास पर नंगे पांव टहलना एवं मधुर व सारगर्भित वार्तालाप।',
    category: 'Psychological & Breathwork',
    gem: 'Emerald (Panna)',
    subGem: 'Peridot / Green Tourmaline',
    metal: 'Gold or Bronze',
    finger: 'Little Finger (Kanishtha)',
    vedicMantra: 'ॐ उद्बुध्यस्वाग्ने प्रति जागृहि त्वमिष्टापूर्ते सꣳसृजेथामयं च। अस्मिन्त्सधस्थे अध्युत्तरस्मिन् विश्वे देवा यजमानश्च सीदत॥',
    beejMantra: 'ॐ ब्रां ब्रीं ब्रौं सः बुधाय नमः (Om Braam Breem Broum Sah Budhaya Namah)',
    daanEn: 'Green gram (Moong Dal), green vegetables, stationery, books, green clothing to students or eunuchs.',
    daanHi: 'हरी मूंग दाल, हरी सब्जियां, पुस्तकें, शिक्षण सामग्री, हरे वस्त्र विद्यार्थियों या किन्नरों को दान।',
    recipientEn: 'Students, young scholars, sister/aunts, or cow shelters (Gaushala) on Wednesday.',
    recipientHi: 'विद्यार्थी, बहन/बुआ अथवा बुधवार को गौशाला में हरा चारा।',
    day: 'Wednesday',
    deityEn: 'Lord Vishnu / Goddess Saraswati / Lord Ganesha',
    deityHi: 'भगवान श्री हरि विष्णु / माँ सरस्वती / श्री गणेश',
    stotram: 'Vishnu Sahasranama & Budha Stotram',
    fastDayEn: 'Wednesday',
    fastDayHi: 'बुधवार व्रत'
  },
  Jupiter: {
    somaticEn: 'Mentoring/teaching others, studying philosophy and ethical texts, practicing gratitude before meals, waking up during Brahma Muhurta, applying saffron (kesar) tilak on forehead.',
    somaticHi: 'विद्यादान/परामर्श, दार्शनिक ग्रंथों का अध्ययन, भोजन से पूर्व कृतज्ञता प्रार्थना, ब्रह्म मुहूर्त में जागरण एवं नाभि/माथे पर केसर-चंदन तिलक।',
    category: 'Habitual Discipline',
    gem: 'Yellow Sapphire (Pukhraj)',
    subGem: 'Yellow Topaz / Heliodor',
    metal: 'Gold or Brass',
    finger: 'Index Finger (Tarjani)',
    vedicMantra: 'ॐ बृहस्पते अति यदर्यो अर्हाद् द्युमद्विभाति क्रतुमज्जनेषु। यद्दीदयच्छवस ऋतप्रजात तदस्मासु द्रविणं धेहि चित्रम्॥',
    beejMantra: 'ॐ ग्रां ग्रीं ग्रौं सः गुरवे नमः (Om Graam Greem Groum Sah Gurave Namah)',
    daanEn: 'Chickpeas (Chana Dal), turmeric, yellow clothes, gold, religious books, saffron to preceptors.',
    daanHi: 'चना दाल, हल्दी, पीले वस्त्र, बेसन की मिठाई, धार्मिक ग्रंथ गुरुजनों या ब्राह्मणों को दान।',
    recipientEn: 'Teachers, spiritual gurus, elderly saints, or temple priests on Thursdays.',
    recipientHi: 'शिक्षक, गुरुजन, वृद्ध संत अथवा गुरुवार को मंदिर के पुजारी।',
    day: 'Thursday',
    deityEn: 'Lord Dakshinamurthy / Lord Brahma / Brihaspati Dev',
    deityHi: 'भगवान दक्षिणामूर्ति / श्री हरि गुरुदेव / बृहस्पति देव',
    stotram: 'Guru Gita & Brihaspati Kavacham',
    fastDayEn: 'Thursday (yellow fruit and gram diet)',
    fastDayHi: 'गुरुवार (पीले फल व बेसन का आहार)'
  },
  Venus: {
    somaticEn: 'Artistic expressions (painting, music, interior decoration), maintaining clean, scented, and aesthetic surroundings, consuming curd/almonds, respecting feminine energy and partners.',
    somaticHi: 'कलात्मक अभिरुचि (संगीत, चित्रकला, गृह-सज्जा), सुगन्धित व स्वच्छ वातावरण, श्वेत आहार (दही, मिश्री), स्त्री-शक्ति व जीवनसाथी का यथोचित सम्मान।',
    category: 'Dietary Alignment',
    gem: 'Diamond (Heera)',
    subGem: 'White Zircon / Opal',
    metal: 'Platinum, White Gold, or Silver',
    finger: 'Middle Finger (Madhyama) or Ring Finger',
    vedicMantra: 'ॐ अन्नात्परिस्रुतो रसं ब्रह्मणा व्यपिबत्क्षत्रं पयः सोमं प्रजापतिः। ऋतेन सत्यमिन्द्रियं विपानꣳ शुक्रमन्धस इन्द्रस्येन्द्रियमिदं पयोऽमृतं मधु॥',
    beejMantra: 'ॐ द्रां द्रीं द्रौं सः शुक्राय नमः (Om Draam Dreem Droum Sah Shukraya Namah)',
    daanEn: 'White silk, perfumes, ghee, rice, white sandalwood, sugar candies to young women or brides.',
    daanHi: 'श्वेत रेशमी वस्त्र, इत्र/सुगंध, घी, चावल, सौंदर्य प्रसाधन, मिश्री का कन्याओं या सुहागिनों को दान।',
    recipientEn: 'Spouse, artists, underprivileged brides, or Lakshmi temples on Friday.',
    recipientHi: 'जीवनसाथी, कलाकार, निर्धन कन्याओं अथवा शुक्रवार को महालक्ष्मी मंदिर।',
    day: 'Friday',
    deityEn: 'Goddess Mahalakshmi / Goddess Parvati',
    deityHi: 'माँ महालक्ष्मी / भगवती ललिता त्रिपुरसुंदरी',
    stotram: 'Shri Suktam & Mahalakshmi Ashtakam',
    fastDayEn: 'Friday (kheer / white foods, salt-free preferred)',
    fastDayHi: 'शुक्रवार (खीर/दूध का सात्विक आहार)'
  },
  Saturn: {
    somaticEn: 'Disciplined manual labor or routine gardening, rigorous sleep schedule, serving blue-collar workers, consuming sesame seeds/mustard oil, maintaining patience in delayed rewards.',
    somaticHi: 'शारीरिक श्रम, बागवानी, नियमित दिनचर्या, असहाय व श्रमजीवियों की प्रत्यक्ष सेवा, काले तिल/सरसों तेल का संतुलित सेवन एवं दीर्घकालिक धैर्य।',
    category: 'Habitual Discipline',
    gem: 'Blue Sapphire (Neelam)',
    subGem: 'Amethyst / Blue Topaz / Iolite',
    metal: 'Iron, Steel, or Lead-free Panchadhatu',
    finger: 'Middle Finger (Madhyama)',
    vedicMantra: 'ॐ शं नो देवीरभिष्टये आपो भवन्तु पीतये। शं योरभि स्रवन्तु नः॥',
    beejMantra: 'ॐ प्रां प्रीं प्रौं सः शनैश्चराय नमः (Om Praam Preem Proum Sah Shanaishcharaya Namah)',
    daanEn: 'Mustard oil, black sesame (Til), iron utensils, black blankets, footwear, whole urad dal to laborers.',
    daanHi: 'सरसों का तेल (छायापात्र दान), काले तिल, लोहे के बर्तन, काले कंबल, जूते-चप्पल, साबुत उड़द का श्रमजीवियों को दान।',
    recipientEn: 'Construction workers, disabled persons, sanitation workers, or Shani temple on Saturdays.',
    recipientHi: 'सफाईकर्मी, दिव्यांग, मजदूर वर्ग अथवा शनिवार को शनिदेव मंदिर।',
    day: 'Saturday',
    deityEn: 'Lord Hanuman / Lord Shani Dev / Lord Shiva (Bhairava)',
    deityHi: 'श्री हनुमान जी / शनिदेव / भगवान कालभैरव',
    stotram: 'Dasharatha Shani Stotram & Shani Chalisa',
    fastDayEn: 'Saturday (khichdi / sesame diet)',
    fastDayHi: 'शनिवार (उड़द खिचड़ी/तिल का सात्विक आहार)'
  },
  Rahu: {
    somaticEn: 'Deep subconscious grounding, ocean walks, digital detox hours (turning off screens before bed), avoiding intoxicants, decluttering electronics, following clean ethics in technology.',
    somaticHi: 'डिजिटल डिटॉक्स (स्क्रीन समय सीमित करना), नशों से पूर्ण परहेज, पुराने बंद पड़े इलेक्ट्रॉनिक्स हटाना, सात्विक आचरण एवं प्रकृति में भ्रमण।',
    category: 'Habitual Discipline',
    gem: 'Hessonite Garnet (Gomed)',
    subGem: 'Spessartite Garnet / Orange Zircon',
    metal: 'Silver or Ashtadhatu',
    finger: 'Middle Finger (Madhyama)',
    vedicMantra: 'ॐ कया नश्चित्र आ भुवदूती सदावृधः सखा। कया शचिष्ठया वृता॥',
    beejMantra: 'ॐ भ्रां भ्रीं भ्रौं सः राहवे नमः (Om Bhraam Bhreem Bhroum Sah Rahave Namah)',
    daanEn: 'Coconut, black mustard seeds, seven grains (Satnaja), dark blankets, lead, old electrical goods.',
    daanHi: 'जटा वाला नारियल, राई, सप्तधान्य (सात अनाज), नीले/धूमिल कंबल, भैरव जी को नारियल अर्पण।',
    recipientEn: 'Lepers, night security guards, or running water immersion (Jal-Pravah) on Wednesday/Saturday nights.',
    recipientHi: 'कुष्ठ रोगी, रात्रि सुरक्षाकर्मी अथवा जल-प्रवाह।',
    day: 'Saturday / Wednesday Night',
    deityEn: 'Goddess Durga / Lord Bhairava / Lord Narasimha',
    deityHi: 'माँ दुर्गा / भगवान भैरव / श्री नृसिंह भगवान',
    stotram: 'Durga Saptashati & Rahu Kavacham',
    fastDayEn: 'Saturday evening',
    fastDayHi: 'शनिवार सायंकालीन उपवास'
  },
  Ketu: {
    somaticEn: 'Silent meditation (Mauna Vrata), Kundalini yoga, spinal energy alignment, feeding stray dogs, visiting secluded monasteries, letting go of obsessive control over outcomes.',
    somaticHi: 'मौन व्रत का अभ्यास, कुंडलिनी/क्रिया योग, आवारा श्वानों (कुत्तों) को भोजन, एकांत आश्रमों का भ्रमण एवं परिणामों के प्रति अनासक्ति भाव।',
    category: 'Psychological & Breathwork',
    gem: "Cat's Eye (Lehsuniya)",
    subGem: 'Chrysoberyl / Tiger Eye',
    metal: 'Silver or Panchadhatu',
    finger: 'Middle or Little Finger',
    vedicMantra: 'ॐ केतुं कृण्वन्नकेतवे पेशो मर्या अपेशसे। समुषद्भिरजायथाः॥',
    beejMantra: 'ॐ स्रां स्रीं स्रौं सः केतवे नमः (Om Sraam Sreem Sroum Sah Ketave Namah)',
    daanEn: 'Multi-colored blankets, sesame seeds, sour fruits, feeding multi-colored dogs, donating to ashrams.',
    daanHi: 'दो-रंगे कंबल, तिल, नींबू/खट्टे फल, चितकबरे श्वान को रोटी, धार्मिक ध्वज (झंडा) मंदिर में अर्पण।',
    recipientEn: 'Monks, wandering ascetics, stray animals on Tuesday or Thursday mornings.',
    recipientHi: 'सन्यासी, फकीर, बेसहारा पशु-पक्षी अथवा मंदिर का शिखर।',
    day: 'Tuesday / Thursday',
    deityEn: 'Lord Ganesha / Lord Matsya Avatar / Bhagavan Shiva',
    deityHi: 'भगवान श्री गणेश / मत्स्य अवतार / महायोगी शिव',
    stotram: 'Ganesha Atharvashirsha & Ketu Kavacham',
    fastDayEn: 'Tuesday or Sankashti Chaturthi',
    fastDayHi: 'संकष्टी चतुर्थी अथवा मंगलवार'
  },
  Ascendant: {
    somaticEn: 'Cultivating core posture, vitality walks, staying attuned to personal body rhythm.',
    somaticHi: 'शरीर की प्राकृतिक लय का सम्मान, नियमित व्यायाम एवं आत्म-सम्मान की रक्षा।',
    category: 'Physical Activity',
    gem: 'Lagna Lord Gemstone',
    subGem: 'Semi-precious substitute of Lagna Lord',
    metal: 'Gold / Silver depending on Lord',
    finger: 'Appropriate to Lord',
    vedicMantra: 'ॐ गायत्री मन्त्र',
    beejMantra: 'ॐ नमः शिवाय',
    daanEn: 'Food and water to travelers',
    daanHi: 'जल एवं अन्न का सात्विक दान',
    recipientEn: 'Needy individuals',
    recipientHi: 'आवश्यकतानुसार जरूरतमंद',
    day: 'Day of Lagna Lord',
    deityEn: 'Ishta Devata / Lagna Lord Deity',
    deityHi: 'इष्ट देवता / लग्नेश के अधिष्ठाता',
    stotram: 'Gayatri Mantra & Navagraha Stotram',
    fastDayEn: 'Lagna Lord Day',
    fastDayHi: 'लग्नेश का दिन'
  }
};

export const generateComprehensiveRemedialPlan = (
  kundali: CompleteKundali,
  chartData: KundaliChartData,
  activeHouseNumber: number = 1
): ComprehensiveRemedialReport => {
  const ascSign =
    kundali.charts?.D1?.houses?.find(h => h.houseNumber === 1)?.sign ||
    kundali.houses?.[0]?.sign ||
    kundali.planets?.find(p => p.name === 'Ascendant')?.sign ||
    (kundali.birthDetails as any)?.ascendantSign ||
    'Aries';
  
  // 1. Generate Cumulative Natal Foundation Remedies for all 9 Navagrahas
  const planetsOrder: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
  
  const cumulativePlanetaryRemedies: PlanetaryRemedyDetail[] = planetsOrder.map(planetName => {
    const lordship = evaluateParashariLordship(planetName, ascSign as any);
    const pos = chartData.planets?.find(p => p.name === planetName);
    const rule = PLANETARY_SOMATIC_RULES[planetName] || PLANETARY_SOMATIC_RULES.Sun;
    
    const isBenefic = lordship.isFunctionalBenefic;
    const isAfflicted = pos?.dignity === 'Debilitated' || pos?.isCombust || pos?.isRetrograde;
    
    // Determine priority
    let priority: 'High' | 'Medium' | 'Low' = 'Medium';
    if (lordship.isYogakaraka || planetName === chartData.planets.find(p => p.house === 1)?.signLord) {
      priority = 'High';
    } else if (pos?.dignity === 'Debilitated' || lordship.nature === 'Maraka' || lordship.isFunctionalMalefic || lordship.isPureDusthanaLord) {
      priority = 'High';
    } else if (pos?.dignity === 'Exalted' || pos?.dignity === 'Own House') {
      priority = 'Low';
    }

    return {
      planet: planetName,
      sanskritName: getPlanetSanskritName(planetName),
      role: `${lordship.nature} (${pos?.dignity || 'Nominal'})`,
      functionalStatus: lordship.nature,
      isBenefic,
      priority,
      somaticBehavioral: {
        en: rule.somaticEn,
        hi: rule.somaticHi,
        category: rule.category
      },
      gemstoneAndAlternative: {
        primaryGem: isBenefic ? rule.gem : 'Contraindicated',
        substituteGem: isBenefic ? rule.subGem : 'None (No substitute gemstone prescribed)',
        metal: rule.metal,
        wearingFinger: rule.finger,
        suitabilityNote: {
          en: isBenefic 
            ? `[PROJECT_INTERPRETATION] Recommended to strengthen auspicious functional benefic energy under project Anukul-vada policy (BPHS Ch. 34 defines functional lordship).`
            : `[PROJECT_INTERPRETATION] [CONTRAINDICATION] ${planetName} is a functional malefic or pure Dusthana lord for ${ascSign} Lagna under BPHS Ch. 34 lordship rules. Gemstone and substitute gemstone wearing is strictly contraindicated under project Anukul-vada policy (BPHS Ch. 34 defines functional lordship, while gemstone prohibition is an interpretive remedial convention). Safe pacification (Mantra, Daana, Seva) should be prioritized instead of gemstone radiation.`,
          hi: isBenefic
            ? `[परियोजना_विवेचना] परियोजना के अनुकूल-वाद नीति अनुसार शुभ भावेश ऊर्जा को प्रगाढ़ करने हेतु उपयुक्त (BPHS अ. ३४ भावेश्वर नियम)।`
            : `[परियोजना_विवेचना] [सावधानी/निषेध] ${ascSign} लग्न हेतु ${planetName} अकारक अथवा त्रिक भावों का स्वामी है (BPHS अ. ३४)। परियोजना के अनुकूल-वाद नियमानुसार रत्न अथवा उपरत्न धारण पूर्णतः वर्जित है (BPHS अ. ३४ कार्यात्मक भावेश्वर निश्चित करता है, रत्न निषेध परियोजना विवेचना है)। केवल सात्त्विक जप एवं दान द्वारा शांति करें।`
        }
      },
      mantraUpasana: {
        vedicMantra: rule.vedicMantra,
        tantricBeejMantra: rule.beejMantra,
        recitationCount: planetName === 'Saturn' ? 23000 : planetName === 'Rahu' ? 18000 : 108,
        recommendedTime: rule.day + ' mornings / evenings'
      },
      daanAndCharity: {
        itemsToDonate: { en: rule.daanEn, hi: rule.daanHi },
        recipientOrLocation: { en: rule.recipientEn, hi: rule.recipientHi },
        idealDay: rule.day,
        warningNote: isBenefic ? {
          en: 'Do not donate gemstone or auspicious metals of your functional benefics to avoid depleting their strength.',
          hi: 'शुभ व योगकारक ग्रहों से संबंधित धातुओं या रत्नों का दान न करें, केवल सेवा भाव रखें।'
        } : undefined
      },
      deityAndDevotion: {
        primaryDeity: { en: rule.deityEn, hi: rule.deityHi },
        stotram: rule.stotram,
        fastingDay: { en: rule.fastDayEn, hi: rule.fastDayHi }
      }
    };
  });

  // 2. Generate Dynamic Contextual Action Plan (Dasha, Sade Sati & Transits)
  const dynamicActionPlan: DynamicTimingRemedy[] = [];
  
  // A. Current Mahadasha Lord
  const currentMd = kundali.dashas?.find(d => d.isCurrent) || kundali.dashas?.[0];
  if (currentMd) {
    const mdPlanet = currentMd.planet as PlanetName;
    const rule = PLANETARY_SOMATIC_RULES[mdPlanet] || PLANETARY_SOMATIC_RULES.Sun;
    dynamicActionPlan.push({
      contextType: 'Mahadasha',
      planet: mdPlanet,
      titleEn: `Active Mahadasha: ${mdPlanet} (Macro Cycle)`,
      titleHi: `सक्रिय महादशा: ${getPlanetSanskritName(mdPlanet)} (दीर्घकालिक चक्र)`,
      urgency: 'Immediate',
      timeframe: `${currentMd.startDate || ''} – ${currentMd.endDate || ''}`,
      impactZone: 'Overall Life Architecture & Major Themes',
      actionPlan: {
        en: `Direct your long-term focus toward ${mdPlanet}'s positive signification. Recite the Beej Mantra daily for optimal harmonic resonance.`,
        hi: `${getPlanetSanskritName(mdPlanet)} के शुभ फल प्राप्ति हेतु नित्य बीज मंत्र का 108 बार जाप करें एवं इसकी प्राकृतिक ऊर्जा के अनुकूल जीवनशैली अपनाएं।`
      },
      somaticAction: {
        en: rule.somaticEn,
        hi: rule.somaticHi
      },
      daanOrPuja: {
        en: `Perform ${rule.stotram} on ${rule.day}s. Support: ${rule.daanEn}`,
        hi: `${rule.day} को ${rule.stotram} का पाठ करें। दान: ${rule.daanHi}`
      }
    });
  }

  // B. Current Antardasha Lord
  const currentAd = currentMd?.antardashas?.find(a => a.isCurrent) || currentMd?.antardashas?.[0];
  if (currentAd && currentAd.planet !== currentMd?.planet) {
    const adPlanet = currentAd.planet as PlanetName;
    const rule = PLANETARY_SOMATIC_RULES[adPlanet] || PLANETARY_SOMATIC_RULES.Moon;
    dynamicActionPlan.push({
      contextType: 'Antardasha',
      planet: adPlanet,
      titleEn: `Active Antardasha: ${adPlanet} (Micro Window)`,
      titleHi: `सक्रिय अंतर्दशा: ${getPlanetSanskritName(adPlanet)} (तात्कालिक प्रभाव)`,
      urgency: 'Active Window',
      timeframe: `${currentAd.startDate || ''} – ${currentAd.endDate || ''}`,
      impactZone: 'Immediate Daily Life, Career & Health Shifts',
      actionPlan: {
        en: `This period is executing sub-karma of ${adPlanet}. Harmonize the mutual relationship between MD (${currentMd?.planet}) and AD (${adPlanet}).`,
        hi: `वर्तमान समय में ${getPlanetSanskritName(adPlanet)} का प्रत्यक्ष प्रभाव सक्रिय है। महादशा व अंतर्दशा स्वामियों में सामंजस्य स्थापित करें।`
      },
      somaticAction: {
        en: rule.somaticEn,
        hi: rule.somaticHi
      },
      daanOrPuja: {
        en: `Light a lamp (Deepam) and offer ${rule.daanEn} on ${rule.day}s.`,
        hi: `${rule.day} को दीपक प्रज्वलित करें एवं ${rule.daanHi} करें।`
      }
    });
  }

  // C. Sade Sati / Dhaiyya Context
  if (kundali.sadeSati?.isInSadeSati || kundali.sadeSati?.isDhaiyya) {
    const saturnRule = PLANETARY_SOMATIC_RULES.Saturn;
    const isSadeSati = kundali.sadeSati.isInSadeSati;
    dynamicActionPlan.push({
      contextType: 'SadeSati',
      planet: 'Saturn',
      titleEn: isSadeSati ? `Shani Sade Sati (${kundali.sadeSati.phase})` : `Shani Kantaka / Ashtama Dhaiyya`,
      titleHi: isSadeSati ? `शनि साढ़े साती (${kundali.sadeSati.phase})` : `शनि ढैय्या प्रभाव`,
      urgency: 'Immediate',
      timeframe: kundali.sadeSati.activePeriodDetails?.formattedEndDate ? `Active until ${kundali.sadeSati.activePeriodDetails.formattedEndDate}` : 'Active Period',
      impactZone: 'Patience, Endurance, Karmic Realignment & Humility',
      actionPlan: {
        en: 'Saturn demands absolute discipline, humility, and zero procrastination. Avoid conflicts with laborers or sub-ordinates.',
        hi: 'शनि निष्ठा, सत्य एवं सेवा भाव की परीक्षा लेते हैं। श्रमिकों से विवाद न करें एवं नित्य दशरथ कृत शनि स्तोत्र का पाठ करें।'
      },
      somaticAction: {
        en: saturnRule.somaticEn,
        hi: saturnRule.somaticHi
      },
      daanOrPuja: {
        en: 'Offer mustard oil lamp beneath Peepal tree on Saturdays. Donate footwear, black blankets to elderly workers.',
        hi: 'शनिवार सायंकाल पीपल वृक्ष के समीप सरसों के तेल का दीपक लगाएं एवं जरूरतमंदों को जूते-चप्पल व कंबल दान करें।'
      }
    });
  }

  // 3. House Remedies Map
  const houseRemedies: Record<number, any> = {};
  const ZODIAC_LORDS = ['Mars', 'Venus', 'Mercury', 'Moon', 'Sun', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Saturn', 'Jupiter'];
  const ascIdx = chartData.ascendantIndex ?? 0;

  for (let h = 1; h <= 12; h++) {
    const signIdx = (ascIdx + h - 1) % 12;
    const hLord = ZODIAC_LORDS[signIdx] as PlanetName;
    const occs = chartData.planets?.filter(p => p.house === h && p.name !== 'Ascendant').map(p => p.name as PlanetName) || [];
    const isDashaImpacting = currentMd?.planet === hLord || currentAd?.planet === hLord || occs.includes(currentMd?.planet as any) || occs.includes(currentAd?.planet as any);
    const rule = PLANETARY_SOMATIC_RULES[hLord] || PLANETARY_SOMATIC_RULES.Sun;

    houseRemedies[h] = {
      houseNum: h,
      houseLord: hLord,
      occupants: occs,
      dashaInfluenced: isDashaImpacting,
      primaryKarmicFocus: {
        en: `Fortify Lord ${hLord} (${rule.gem}) and harmonize ${occs.length > 0 ? occs.join(', ') : 'house themes'}.`,
        hi: `भावेश ${getPlanetSanskritName(hLord)} को बल दें एवं ${occs.length > 0 ? occs.map(getPlanetSanskritName).join(', ') : 'भाव के कारकों'} को संतुलित करें।`
      },
      behavioralAdjustment: {
        en: rule.somaticEn,
        hi: rule.somaticHi
      }
    };
  }

  return {
    lagnaSign: ascSign,
    currentDashaSummary: `${currentMd?.planet || ''} - ${currentAd?.planet || ''}`,
    cumulativePlanetaryRemedies,
    dynamicActionPlan,
    houseRemedies
  };
};

const getPlanetSanskritName = (p: PlanetName): string => {
  const map: Record<PlanetName, string> = {
    Sun: 'सूर्य (Surya)',
    Moon: 'चन्द्र (Chandra)',
    Mars: 'मंगल (Mangal)',
    Mercury: 'बुध (Budha)',
    Jupiter: 'बृहस्पति (Brihaspati)',
    Venus: 'शुक्र (Shukra)',
    Saturn: 'शनि (Shani)',
    Rahu: 'राहु (Rahu)',
    Ketu: 'केतु (Ketu)',
    Ascendant: 'लग्न (Lagna)'
  };
  return map[p] || p;
};
